import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Share2, UserPlus } from "lucide-react";
import { fetchReferralsMe, type ReferralInvitee, type ReferralStatus } from "../api/referrals";
import { AtlasPanel } from "./AtlasPanel";
import { Badge } from "./Badge";
import { Button } from "./Button";
import { EmptyState } from "./EmptyState";
import { Skel } from "./Skeleton";

// "Your invites" (spec 21 / ATL-96): the referrer's side of invite a friend.
// Anchored at /profile#invites. Uses the ["referrals", "me"] key shared with mobile.

export const REFERRALS_ME_KEY = ["referrals", "me"];

const SHARE_TEXT =
  "I keep my hills on the record with Atlas. Join with my link and we both get 200 Waypoints once your first walk is logged.";

const STATUS_LABEL: Record<ReferralStatus, string> = {
  pending: "Waiting for a first walk",
  review: "Waiting for a first walk",
  credited: "Credited",
  rejected: "Not eligible",
  expired: "Expired",
};

function statusVariant(status: ReferralStatus): "gold" | "neutral" | "info" {
  if (status === "credited") return "gold";
  if (status === "pending" || status === "review") return "info";
  return "neutral";
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function formatDay(value: string): string {
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function InviteeRow({ invitee }: { invitee: ReferralInvitee }) {
  const when =
    invitee.status === "credited" && invitee.creditedAt
      ? `Credited ${formatDay(invitee.creditedAt)}`
      : `Joined ${formatDay(invitee.createdAt)}`;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "9px 0",
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {invitee.label}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>{when}</div>
      </div>
      <Badge variant={statusVariant(invitee.status)}>{STATUS_LABEL[invitee.status]}</Badge>
    </div>
  );
}

export function InvitesPanel() {
  const query = useQuery({
    queryKey: REFERRALS_ME_KEY,
    queryFn: fetchReferralsMe,
    staleTime: 60 * 1000,
  });
  const [copied, setCopied] = React.useState(false);
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const data = query.data;
  const link = data?.link ?? null;
  const reward = data?.reward ?? 200;

  function copyLink() {
    if (!link) return;
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function shareLink() {
    if (!link || !canShare) return;
    void navigator
      .share({ title: "Join me on Atlas", text: SHARE_TEXT.replace("200", String(reward)), url: link })
      .catch(() => {
        // User dismissed the sheet; nothing to do.
      });
  }

  let countsLine: string | null = null;
  if (data) {
    const waiting = data.counts.pending + data.counts.review;
    const joined = waiting + data.counts.credited;
    if (joined > 0) {
      const parts = [plural(joined, "joined", "joined"), plural(data.counts.credited, "credited", "credited")];
      if (waiting > 0) parts.push(`${waiting} waiting for a first walk`);
      countsLine = parts.join(" · ");
    }
  }

  return (
    <div id="invites" style={{ scrollMarginTop: 24 }}>
      <AtlasPanel title="Your invites">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0, lineHeight: 1.5, maxWidth: 520 }}>
            Both of you get {reward} Waypoints once their first walk of at least 1 km is on the
            record. Rewards settle for up to {data?.monthCap.limit ?? 10} invites a month.
          </p>

          {query.isLoading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Skel height={36} radius="var(--radius-md)" />
              <Skel width="60%" height={14} />
            </div>
          )}

          {query.isError && (
            <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
              Couldn't load your invites just now.
            </p>
          )}

          {data && !link && (
            <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0 }}>
              <a href="#public-profile" style={{ color: "var(--text-accent)", fontWeight: 600 }}>
                Claim a username
              </a>{" "}
              to get your invite link.
            </p>
          )}

          {data && link && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>
                Your invite link
              </label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} className="r-wrap">
                <input
                  readOnly
                  value={link}
                  onFocus={(e) => e.currentTarget.select()}
                  aria-label="Invite link"
                  style={{
                    flex: "1 1 240px",
                    minWidth: 0,
                    height: "var(--control-md)",
                    padding: "0 12px",
                    background: "var(--surface-sunken)",
                    border: "1px solid var(--border-default)",
                    borderRadius: "var(--radius-md)",
                    color: "var(--text-primary)",
                    fontSize: 13,
                    fontFamily: "var(--font-mono)",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    variant="secondary"
                    leftIcon={copied ? <Check size={14} /> : <Copy size={14} />}
                    onClick={copyLink}
                  >
                    {copied ? "Copied" : "Copy"}
                  </Button>
                  {canShare && (
                    <Button variant="ghost" leftIcon={<Share2 size={14} />} onClick={shareLink}>
                      Share
                    </Button>
                  )}
                </div>
              </div>
              {countsLine && (
                <div style={{ fontSize: 13, color: "var(--text-secondary)" }} className="tnum">
                  {countsLine}
                </div>
              )}
              {data.monthCap.used > 0 && (
                <div style={{ fontSize: 12, color: "var(--text-muted)" }} className="tnum">
                  {data.monthCap.used} / {data.monthCap.limit} credited this month
                </div>
              )}
            </div>
          )}

          {data && (
            data.invitees.length === 0 ? (
              <EmptyState
                icon={<UserPlus size={20} />}
                title="No one has joined from your link yet."
                hint={link ? "Send it to someone who keeps saying they'll get round to it." : undefined}
              />
            ) : (
              <div>
                {data.invitees.map((invitee, i) => (
                  <InviteeRow key={`${invitee.createdAt}-${i}`} invitee={invitee} />
                ))}
              </div>
            )
          )}
        </div>
      </AtlasPanel>
    </div>
  );
}
